/** Data kalendarzowa bez strefy czasowej, w formacie `YYYY-MM-DD`. */
export type IsoDate = string

const ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const

export function parseIso(value: IsoDate): { year: number; month: number; day: number } {
  const match = ISO_PATTERN.exec(value)
  if (!match) throw new Error(`Nieprawidłowa data: ${value}`)
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  }
}

export function toIso(year: number, month: number, day: number): IsoDate {
  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  return `${year}-${mm}-${dd}`
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28
  const length = MONTH_LENGTHS[month - 1]
  if (length === undefined) throw new Error(`Nieprawidłowy miesiąc: ${month}`)
  return length
}

export function clampDay(year: number, month: number, day: number): number {
  return Math.min(day, daysInMonth(year, month))
}

export function addMonths(value: IsoDate, months: number): IsoDate {
  const { year, month, day } = parseIso(value)
  const zeroBased = year * 12 + (month - 1) + months
  const targetYear = Math.floor(zeroBased / 12)
  const targetMonth = (zeroBased % 12) + 1
  return toIso(targetYear, targetMonth, clampDay(targetYear, targetMonth, day))
}

export function addDays(value: IsoDate, days: number): IsoDate {
  const { year, month, day } = parseIso(value)
  const utc = Date.UTC(year, month - 1, day + days)
  const shifted = new Date(utc)
  return toIso(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate())
}

export function compareIso(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0
}

export function startOfMonth(year: number, month: number): IsoDate {
  return toIso(year, month, 1)
}

export function endOfMonth(year: number, month: number): IsoDate {
  return toIso(year, month, daysInMonth(year, month))
}

export function startOfYear(year: number): IsoDate {
  return toIso(year, 1, 1)
}

export function endOfYear(year: number): IsoDate {
  return toIso(year, 12, 31)
}

export function isWeekend(value: IsoDate): boolean {
  const { year, month, day } = parseIso(value)
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  return weekday === 0 || weekday === 6
}

/** Cofa datę do najbliższego wcześniejszego dnia roboczego (lub zwraca ją bez zmian). */
export function previousBusinessDay(value: IsoDate): IsoDate {
  let current = value
  while (isWeekend(current)) current = addDays(current, -1)
  return current
}

export function todayIso(now: Date = new Date()): IsoDate {
  return toIso(now.getFullYear(), now.getMonth() + 1, now.getDate())
}
